import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchBrands, BrandSearchResult } from '../../services/brandService';
import './SearchSuggestions.css';

interface SearchSuggestionsProps {
  searchInput: string;
  onSelectSuggestion: (suggestion: string) => void;
  isOpen: boolean;
}

function SearchSuggestions({ searchInput, onSelectSuggestion, isOpen }: SearchSuggestionsProps) {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState<BrandSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen || !searchInput || searchInput.trim().length === 0) {
      setSuggestions([]);
      setSelectedIndex(-1);
      return;
    }

    const loadSuggestions = async () => {
      setLoading(true);
      const results = await searchBrands(searchInput);
      setSuggestions(results);
      setSelectedIndex(-1);
      setLoading(false);
    };

    const debounceTimer = setTimeout(() => {
      loadSuggestions();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchInput, isOpen]);

  const handleSuggestionClick = (suggestion: BrandSearchResult) => {
    // Determine the URL path based on suggestion type
    if (suggestion.type === 'brand') {
      navigate(`/dashboard/opportunitydetails/${encodeURIComponent(suggestion.name)}`);
    } else if (suggestion.type === 'parent') {
      navigate(`/dashboard/opportunitydetails/${encodeURIComponent(suggestion.name)}?type=ultimateparent`);
    }

    // Call parent callback and clear suggestions
    onSelectSuggestion(suggestion.name);
    setSuggestions([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setSuggestions([]);
        break;
    }
  };

  if (!isOpen || suggestions.length === 0) {
    return null;
  }

  return (
    <div
      className="search-suggestions-dropdown"
      ref={suggestionsRef}
      onKeyDown={handleKeyDown}
    >
      {loading ? (
        <div className="suggestions-loading">
          <span>Searching...</span>
        </div>
      ) : (
        <div className="suggestions-list">
          {suggestions.map((suggestion, index) => (
            <div
              key={`${suggestion.type}-${suggestion.name}`}
              className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSuggestionClick(suggestion);
              }}
            >
              <div className="suggestion-content">
                <div className="suggestion-name">{suggestion.name}</div>
                {suggestion.type === 'brand' && suggestion.ultimate_parent && (
                  <div className="suggestion-parent">
                    <span className="parent-label">Ultimate Parent:</span> {suggestion.ultimate_parent}
                  </div>
                )}
              </div>
              <div className={`suggestion-type ${suggestion.type}`}>
                {suggestion.type === 'brand' ? 'Brand' : 'Ultimate Parent'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SearchSuggestions;
